"use client";

import * as React from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Drawer,
  DrawerContent,
  DrawerDescription,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
} from "@/components/ui/drawer";
import { useIsMobile } from "@/hooks/use-mobile";
import { cn } from "@/lib/utils";

type DrawerHeight = "fit" | "medium" | "tall" | "full";

const drawerHeightClasses: Record<DrawerHeight, string> = {
  fit: "h-auto",
  medium: "max-h-[60vh]",
  tall: "max-h-[85vh]",
  full: "h-full",
};

const dialogHeightClasses: Record<DrawerHeight, string> = {
  fit: "",
  medium: "max-h-[60vh]",
  tall: "max-h-[85vh]",
  full: "max-h-[95vh]",
};

interface ResponsiveModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description?: string;
  showCloseButton?: boolean;
  srOnlyHeader?: boolean;
  className?: string;
  drawerHeight?: DrawerHeight;
  footer?: React.ReactNode;
  children?: React.ReactNode;
}

export function ResponsiveModal({
  open,
  onOpenChange,
  title,
  description,
  showCloseButton = true,
  srOnlyHeader = false,
  className,
  drawerHeight = "fit",
  footer,
  children,
}: ResponsiveModalProps) {
  const isMobile = useIsMobile();

  if (isMobile) {
    return (
      <Drawer open={open} onOpenChange={onOpenChange}>
        <DrawerContent className={cn(drawerHeightClasses[drawerHeight])}>
          <DrawerHeader className={srOnlyHeader ? "sr-only" : "text-center"}>
            <DrawerTitle>{title}</DrawerTitle>
            {description && (
              <DrawerDescription>{description}</DrawerDescription>
            )}
          </DrawerHeader>
          {children && (
            <div
              className={cn(
                "overflow-y-auto px-4",
                // When there's no footer, the children scroll area is the last
                // thing in the drawer — give it the same safe-area-aware bottom
                // padding the DrawerFooter would have applied, so content never
                // collides with the iOS home indicator.
                !footer &&
                  "pb-[max(var(--loc-safe-area-inset-bottom),16px)]",
              )}
            >
              {children}
            </div>
          )}
          {footer && (
            <DrawerFooter className="pb-[max(var(--loc-safe-area-inset-bottom),16px)]">
              {footer}
            </DrawerFooter>
          )}
        </DrawerContent>
      </Drawer>
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        showCloseButton={showCloseButton}
        className={cn(
          "sm:max-w-md flex flex-col",
          dialogHeightClasses[drawerHeight],
          className,
        )}
      >
        <DialogHeader className={srOnlyHeader ? "sr-only" : undefined}>
          <DialogTitle>{title}</DialogTitle>
          {description && (
            <DialogDescription>{description}</DialogDescription>
          )}
        </DialogHeader>
        {children && (
          <div className="overflow-y-auto -mx-6 px-6 min-h-0 flex-1">
            {children}
          </div>
        )}
        {footer && <DialogFooter>{footer}</DialogFooter>}
      </DialogContent>
    </Dialog>
  );
}
